import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, 
  Alert, LinearProgress, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Chip
} from '@mui/material';
import { CloudUpload, Download } from '@mui/icons-material';
import { bulkUploadSamples, clearBulkUploadResult } from '../store/sampleSlice';

const BulkUpload = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { loading, bulkUploadResult } = useSelector(state => state.samples);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = (file) => {
    if (!file) return;
    
    // Validate file type
    if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
      alert('Please select a CSV file only.');
      return;
    }
    
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB.');
      return;
    }
    
    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    try {
      await dispatch(bulkUploadSamples(selectedFile)).unwrap();
      onSuccess?.();
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    dispatch(clearBulkUploadResult());
    onClose();
  };

  const downloadTemplate = () => {
    const csvContent = 'labId,sampleName,regularity,storageTemperature,departments,createdDate,createdBy,chemicalStatus,chemicalHOD,chemicalHodAssignedAt,chemicalHodAcceptedAt,microStatus,microHOD,microHodAssignedAt,microHodAcceptedAt,instrumentStatus,instrumentHOD,instrumentHodAssignedAt,instrumentHodAcceptedAt,gmStatus,gmHOD,gmHodAssignedAt,gmHodAcceptedAt\n' +
      'LAB-2024-001,Water Quality Sample,General,Ambient,Chemical;Micro,15-01-2024 09:00,Admin User,Pending,,15-01-2024 10:00,,Pending,,15-01-2024 10:00,,N/A,,,,,N/A,,,\n' +
      'LAB-2024-002,Food Safety Sample,FSSAI(central),Freezed,Chemical,16-01-2024 08:30,Admin User,Accepted,Chemical HOD,16-01-2024 09:30,16-01-2024 14:30,N/A,,,,,N/A,,,,,N/A,,,\n' +
      'LAB-2024-003,Instrument Calibration,General,Deep Freezed,Instrument;GM,17-01-2024 14:00,Admin User,N/A,,,,,N/A,,,,,Pending,,17-01-2024 15:00,,Pending,,17-01-2024 15:00,';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Upload Samples</DialogTitle>
      <DialogContent>
        {!bulkUploadResult ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Button
                startIcon={<Download />}
                onClick={downloadTemplate}
                variant="outlined"
                size="small"
              >
                Download CSV Template
              </Button>
            </Box>

            <Box
              sx={{
                border: '2px dashed',
                borderColor: dragOver ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                bgcolor: dragOver ? 'action.hover' : 'background.paper',
                cursor: 'pointer'
              }}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => document.getElementById('csv-file-input').click()}
            >
              <CloudUpload sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {selectedFile ? selectedFile.name : 'Drop CSV file here or click to browse'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supported format: CSV files only (Max 5MB)
              </Typography>
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e.target.files[0])}
              />
            </Box>

            {selectedFile && (
              <Alert severity="success" sx={{ mt: 2 }}>
                ✓ File selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </Alert>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>CSV Format Requirements:</Typography>
              <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                <strong>Required columns:</strong> labId, sampleName, storageTemperature, regularity, departments<br/>
                <strong>Storage Temperature:</strong> Ambient, Freezed, Deep Freezed<br/>
                <strong>Regularity:</strong> BIS, FSSAI, Food, General, Subcontract, IQC, IOPEPC<br/>
                <strong>Departments:</strong> Chemical;Micro;Instrument;GM (semicolon separated)<br/>
                <strong>Date Format:</strong> DD-MM-YYYY HH:mm (e.g., 15-01-2024 09:00)<br/>
                <strong>Optional:</strong> All date fields, status fields, user names
              </Typography>
              <Typography variant="body2" color="text.secondary">
                💡 Tip: Export existing samples to get the exact format, then modify and re-import.
              </Typography>
            </Box>
          </>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              {bulkUploadResult.message}
            </Alert>
            
            {bulkUploadResult.results?.success?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Successfully Created ({bulkUploadResult.results.success.length})
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Lab ID</TableCell>
                        <TableCell>Sample Name</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkUploadResult.results.success.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.row}</TableCell>
                          <TableCell>{item.labId}</TableCell>
                          <TableCell>{item.sampleName}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {bulkUploadResult.results?.errors?.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom color="error">
                  Errors ({bulkUploadResult.results.errors.length})
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkUploadResult.results.errors.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.row}</TableCell>
                          <TableCell>
                            <Chip label={item.error} color="error" size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}

        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </DialogContent>
      <DialogActions>
        {!bulkUploadResult ? (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button 
              onClick={handleUpload} 
              variant="contained" 
              disabled={!selectedFile || loading}
            >
              {loading ? 'Uploading...' : 'Upload'}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained">Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkUpload;